import React, { useState, useEffect, useMemo } from 'react';
import { Container, Grid, Typography, TextField, Button, Box, CircularProgress, Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import Confetti from 'react-confetti';
import { useSelector } from 'react-redux';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL 

const FAQ = () => {
  const userIdFromRedux = useSelector(state => state.onboarding.userId);
  const userId = userIdFromRedux || localStorage.getItem('userId');
  const [userAnswer, setUserAnswer] = useState('');
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFAQ = async () => {
      if (!userId) {
        window.location.href = process.env.REACT_APP_LOGIN_URL;
        setLoading(false);
        return;
      }

      try {
        const apiUrl = `${API_BASE_URL}/api/faq`;
        const response = await axios.get(apiUrl, {
          params: { userId }
        });
        setFaqData(response.data);
      } catch (e) {
        const errorMessage = `Failed to fetch FAQ data for userId: ${userId}`;
        setError(errorMessage);
        console.error(errorMessage, e);
        console.error('Error details:', e.response ? e.response.data : 'No response data');
        console.error('Full error object:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQ();
  }, [userId]);

  const unansweredQuestions = useMemo(() => {
    return Array.isArray(faqData) 
      ? faqData.filter(question => !question.answer || question.answer.trim() === '')
      : [];
  }, [faqData]);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const handleAnswerChange = (event) => {
    setUserAnswer(event.target.value);
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [showConfetti, setShowConfetti] = useState(false);

  const handleSubmit = async () => {
    if (!userId) {
      window.location.href = process.env.REACT_APP_LOGIN_URL;
      return;
    }

    if (userAnswer.trim() !== '') {
      try {
        const currentQuestion = unansweredQuestions[currentQuestionIndex];
        const response = await axios.put(`${API_BASE_URL}/api/faq`, {
          userId,
          questionId: currentQuestion.questionId,
          answer: userAnswer
        });
        // Assuming the API always returns a success status if the request was successful
        setFaqData(prevData => prevData.map(q => 
          q.questionId === currentQuestion.questionId ? {...q, answer: userAnswer} : q
        ));

        setUserAnswer('');
        const newIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(newIndex);
        if (newIndex >= unansweredQuestions.length) {
          setShowConfetti(true);
          setSnackbar({
            open: true,
            message: 'Congratulations! You have answered all queries.',
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Answer saved successfully',
            severity: 'success'
          });
        }
      } catch (error) {
        console.error('Error saving answer:', error);
        setSnackbar({
          open: true,
          message: `Failed to save answer: ${error.response?.data?.message || error.message}`,
          severity: 'error'
        });
      }
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const allQuestionsAnswered = currentQuestionIndex >= unansweredQuestions.length;

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <React.Fragment>
      {showConfetti && <Confetti />}
      <Container maxWidth="md" sx={{ mt: 4 }}>
        {!allQuestionsAnswered && unansweredQuestions[currentQuestionIndex] && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">
                {unansweredQuestions[currentQuestionIndex].question}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Your Answer"
                value={userAnswer}
                onChange={handleAnswerChange}
                variant="outlined"
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={userAnswer.trim() === ''}
                >
                  Submit
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        {allQuestionsAnswered && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom>
              Congratulations!!
            </Typography>
            <Typography variant="h6">
              Your AI Talker has been fully trained with your responses
            </Typography>
          </Box>
        )}
      </Container>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <MuiAlert elevation={6} variant="filled" onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </React.Fragment>
  );
};

export default FAQ;
